import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import {
  AdmissionStepSubmitHandle,
  StudentAdmissionStepProps,
} from '../studentAdmission.interfaces';
import { Grid, Button, Input, Divider } from '@mui/material';
import {
  GENDER,
  TPrimaryDetails,
  TUserDetails,
} from '../../../../../../../apis/users/users.interfaces';
import { useForm, FormProvider, Path } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { userDefaultValue } from '../../../../../../../utils/validationHelpers/userValidationSchema';
import ControlledSelectField from '../../../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledFileUploadField from '../../../../../../components/controlledComponents/ControlledFileUploadField.component';
import ControlledTextField from '../../../../../../components/controlledComponents/ControlledTextField.component';
import ControlledCountryCodeSelectField from '../../../../../../components/controlledComponents/ControlledCountryCodeSelectField.component';
import { createPathProxy } from '../../../../../../../utils/reactHookFormPath';
import { useTranslation } from 'src/hooks/useTranslationHook';
import isAppWithMock from 'src/utils/isAppWithMock';

const getTodayISODate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getAdmissionStep1Schema = (translateText: (key: string) => string) =>
  z.object({
    student: z.object({
      id: z.string().optional(),
      basicDetails: z.object({
        firstName: z
          .string()
          .min(2, 'First name must be at least 2 characters')
          .max(50, 'First name must not exceed 50 characters')
          .regex(
            /^[A-Za-z\s]+$/,
            'First name should only contain letters and spaces'
          ),
        middleName: z
          .string()
          .regex(
            /^[A-Za-z\s]*$/,
            'Middle name should only contain letters and spaces'
          )
          .max(50, 'Middle name must not exceed 50 characters')
          .optional().nullable(),
        lastName: z
          .string()
          .min(2, 'Last name must be at least 2 characters')
          .max(50, 'Last name must not exceed 50 characters')
          .regex(
            /^[A-Za-z\s]+$/,
            'Last name should only contain letters and spaces'
          ),
        gender: z
          .union([
            z.literal('select'),
            z.nativeEnum(GENDER, {
              errorMap: () => ({
                message: translateText(
                  'students.admission.validation.genderRequired'
                ),
              }),
            }),
          ])
          .refine((val) => val !== 'select', {
            message: translateText(
              'students.admission.validation.genderRequired'
            ),
          }),
        dateOfBirth: z
          .string()
          .min(
            1,
            translateText('students.admission.validation.dateOfBirthRequired')
          )
          .regex(
            /^\d{4}-\d{2}-\d{2}$/,
            translateText('students.admission.validation.dateOfBirthInvalid')
          )
          .refine((val) => val <= getTodayISODate(), {
            message: translateText(
              'students.admission.validation.dateOfBirthFuture'
            ),
          }),
      }),
      contactDetails: z.object({
        email: z.object({
          email: z
            .string()
            .trim()
            .min(1, translateText('students.admission.validation.emailEmpty'))
            .email(
              translateText('students.admission.validation.emailInvalid')
            )
            .max(
              50,
              translateText('students.admission.validation.emailTooLong')
            ),
        }),
        phone: z.object({
        countryCode: z
          .string()
          .min(1, 'Country code is required')
          .max(4, 'Country code must be at most 4 characters')
          .regex(/^\+[1-9]\d{0,14}$/, 'Invalid country code format'),
        phoneNumber: z
          .string()
          .min(8, 'Phone number must be at least 8 digits')
          .max(15, 'Phone number must not exceed 15 digits')
          .regex(/^\d+$/, 'Phone number should only contain digits'),
      }),
      address: z.object({
        address1: z
          .string()
          .min(5, 'Address must be at least 5 characters')
          .max(200, 'Address must not exceed 200 characters'),
        address2: z
          .string()
          .max(200, 'Address must not exceed 200 characters')
          .refine(
            (val) => !val || val.length >= 5,
            'Address must be at least 5 characters'
          )
          .optional(),
        city: z
          .string()
          .min(2, 'City must be at least 2 characters')
          .max(50, 'City must not exceed 50 characters')
          .regex(/^[A-Za-z\s]+$/, 'City should only contain letters and spaces'),
        state: z
          .string()
          .min(2, 'State must be at least 2 characters')
          .max(50, 'State must not exceed 50 characters')
          .regex(
            /^[A-Za-z\s]+$/,
            'State should only contain letters and spaces'
          ),
        postalCode: z
          .string()
          .min(4, 'Postal code must be at least 4 characters')
          .max(10, 'Postal code must not exceed 10 characters')
          .regex(/^[A-Za-z0-9\s-]+$/, 'Invalid postal code format'),
      }),
      }),
    }),
  });

export type Step1FormData = z.input<
  ReturnType<typeof getAdmissionStep1Schema>
> & {
  student: Partial<TUserDetails>;
};

const defaultValues = {
  student: {
    ...userDefaultValue,
    basicDetails: {
      ...userDefaultValue.basicDetails,
      gender: 'select',
      dateOfBirth: '',
    },
  },
} as unknown as Step1FormData;

const path = createPathProxy<Step1FormData>();

const AdmissionStep1BasicDetailsView = forwardRef<
  AdmissionStepSubmitHandle,
  StudentAdmissionStepProps<Step1FormData>
>(function AdmissionStep1BasicDetailsView(
  { onCompleteHandler, studentDetails, onStepValidityChange, onPersistStep },
  ref
) {
  const isMockData = isAppWithMock();
  const { translateText } = useTranslation();
  const admissionStep1Schema = useMemo(
    () => getAdmissionStep1Schema(translateText),
    [translateText]
  );
  const methods = useForm<Step1FormData>({
    resolver: zodResolver(admissionStep1Schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    control,
  } = methods;

  useImperativeHandle(ref, () => ({
    submitStep: () =>
      new Promise((resolve) => {
        if (!onPersistStep) {
          resolve(true);
          return;
        }
        void handleSubmit(
          async (data) => {
            try {
              await onPersistStep(data);
              resolve(true);
            } catch {
              resolve(false);
            }
          },
          () => resolve(false)
        )();
      }),
  }));

  useEffect(() => {
    onStepValidityChange?.(isValid);
  }, [isValid, onStepValidityChange]);

  useEffect(() => {
    if (studentDetails) {
      const baseContact = userDefaultValue.contactDetails;
      const apiContact = studentDetails.contactDetails;
      reset({
        student: {
          id: studentDetails.id,
          basicDetails: studentDetails.basicDetails as unknown as TPrimaryDetails,
          contactDetails: {
            ...baseContact,
            _id: apiContact._id,
            email: { ...baseContact.email, ...apiContact.email },
            phone: { ...baseContact.phone, ...apiContact.phone },
            address: apiContact.address
              ? {
                ...baseContact.address,
                ...apiContact.address,
                address2: apiContact.address.address2 ?? undefined,
                state: apiContact.address.state ?? undefined,
                country: apiContact.address.country ?? undefined,
                isCurrent: apiContact.address.isCurrent ?? undefined,
                description: apiContact.address.description ?? undefined,
              }
              : baseContact.address,
          },
        },
      });
    } else {
      reset(defaultValues);
    }
  }, [studentDetails, reset]);

  useEffect(() => {
    if (studentDetails && Object.keys(errors).length === 0) {
      const timeoutId = setTimeout(() => {
        onCompleteHandler();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [studentDetails, errors, onCompleteHandler]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit((data) => {
          void onPersistStep?.(data);
        })}
        style={{ width: '100%' }}
        id="admission-step-1-form"
      >
        <Grid
          container
          spacing={{ xs: 1, sm: 2 }}
          padding={{ xs: 1, sm: 2 }}
          data-testid="admission-step-1-form-container"
        >
          <Input
            fullWidth
            {...register('student.id')}
            error={!!errors.student?.id}
            type="hidden"
          />
          <Grid item xs={12} sm={4}>
            <ControlledTextField
              label={translateText('students.admission.firstName')}
              name={String(path.student.basicDetails.firstName)}
              control={control}
              errorMessage={errors.student?.basicDetails?.firstName?.message}
              id="first-name"
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ControlledTextField
              label={translateText('students.admission.middleName')}
              name={String(path.student.basicDetails.middleName)}
              control={control}
              errorMessage={errors.student?.basicDetails?.middleName?.message}
              id="middle-name"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ControlledTextField
              label={translateText('students.admission.lastName')}
              name={String(path.student.basicDetails.lastName)}
              control={control}
              errorMessage={errors.student?.basicDetails?.lastName?.message}
              id="last-name"
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <ControlledSelectField
              label={translateText('students.admission.gender')}
              id="gender"
              isRequired
              name={String(path.student.basicDetails.gender)}
              control={control}
              itemList={[
                {
                  key: GENDER.MALE,
                  value: translateText('students.admission.male'),
                },
                {
                  key: GENDER.FEMALE,
                  value: translateText('students.admission.female'),
                },
                {
                  key: GENDER.OTHER,
                  value: translateText('students.admission.other'),
                },
              ]}
              errorMessage={errors.student?.basicDetails?.gender?.message}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <ControlledTextField
              label={translateText('students.admission.dateOfBirth')}
              type="date"
              name={String(path.student.basicDetails.dateOfBirth)}
              control={control}
              errorMessage={errors.student?.basicDetails?.dateOfBirth?.message}
              id="date-of-birth"
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: getTodayISODate() }}
            />
          </Grid>

          {/* Phone Section */}
          <Grid item xs={12} sm={4}>
            <ControlledCountryCodeSelectField<Step1FormData>
              label={translateText('students.admission.code')}
              name={
                String(path.student.contactDetails.phone.countryCode) as Path<Step1FormData>
              }
              control={control}
              errorMessage={
                errors.student?.contactDetails?.phone?.countryCode?.message
              }
              id="phone-code-label"
              isGridItem={false}
              isRequired
              data-testid="admission-step-1-phone-country-code"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ControlledTextField
              label={translateText('students.admission.phoneNumber')}
              name={String(path.student.contactDetails.phone.phoneNumber)}
              control={control}
              errorMessage={
                errors.student?.contactDetails?.phone?.phoneNumber?.message
              }
              id="phone-number"
              required
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              label={translateText('auth.email')}
              type="email"
              name={String(path.student.contactDetails.email.email)}
              control={control}
              errorMessage={
                errors.student?.contactDetails?.email?.email?.message
              }
              id="email"
              required
            />
          </Grid>

          {/* Address Section */}
          <Grid item xs={12}>
            <ControlledTextField
              label={translateText('students.admission.address1')}
              multiline
              rows={2}
              name={String(
                (path.student.contactDetails.address as any).address1
              )}
              control={control}
              errorMessage={
                errors.student?.contactDetails?.address?.address1?.message
              }
              id="address-1"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField
              label={translateText('students.admission.address2')}
              multiline
              rows={2}
              name={String(
                (path.student.contactDetails.address as any).address2
              )}
              control={control}
              errorMessage={
                errors.student?.contactDetails?.address?.address2?.message
              }
              id="address-2"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ControlledTextField
              fullWidth
              label={translateText('students.admission.city')}
              name={String((path.student.contactDetails.address as any).city)}
              control={control}
              errorMessage={
                errors.student?.contactDetails?.address?.city?.message
              }
              id="city"
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ControlledTextField
              label={translateText('students.admission.state')}
              name={String((path.student.contactDetails.address as any).state)}
              control={control}
              errorMessage={
                errors.student?.contactDetails?.address?.state?.message
              }
              id="state"
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ControlledTextField
              label={translateText('students.admission.postalCode')}
              name={String(
                (path.student.contactDetails.address as any).postalCode
              )}
              control={control}
              errorMessage={
                errors.student?.contactDetails?.address?.postalCode?.message
              }
              id="postal-code"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <ControlledFileUploadField
              label={translateText('students.admission.profilePicture')}
              name={String(path.student.basicDetails.profile.profilePicture)}
              control={control}
              errorMessage={
                errors.student?.basicDetails?.profile?.profilePicture?.message
              }
              id="profile-picture"
              sectionTitle={translateText('students.admission.profilePicture')}
            />
          </Grid>
          {!studentDetails ? (
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
              data-testid="admission-step-1-actions"
            >
              {isMockData ? (
                <Button
                  onClick={() => onCompleteHandler()}
                  sx={{
                    minHeight: { xs: '44px', sm: '36px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                  data-testid="admission-step-1-finish-button"
                >
                  {translateText('students.admission.finishStep1')}
                </Button>
              ) : null}
            </Grid>
          ) : null}
        </Grid>
      </form>
    </FormProvider>
  );
});

export default AdmissionStep1BasicDetailsView;
